import { message } from "antd";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useCallback, useRef, useState } from "react";

interface DownloadOptions {
  fileName?: string;
  scale?: number;
  format?: "a4" | "letter";
  orientation?: "portrait" | "landscape" | "auto";
  title?: string;
}

interface UseDownloadPdfReturn {
  pdfRef: React.RefObject<HTMLDivElement | null>;
  isDownloading: boolean;
  downloadPdf: (options?: DownloadOptions) => Promise<void>;
}

/**
 * Custom hook for downloading HTML content as PDF
 * @example
 * const { pdfRef, isDownloading, downloadPdf } = useDownloadPdf();
 *
 * <div ref={pdfRef}>
 *   <Certificate />
 * </div>
 *
 * <Button onClick={() => downloadPdf({ fileName: 'my-pdf' })} loading={isDownloading}>
 *   Download
 * </Button>
 */
export const useDownloadPdf = (): UseDownloadPdfReturn => {
  const pdfRef = useRef<HTMLDivElement | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadPdf = useCallback(async (options: DownloadOptions = {}) => {
    const {
      fileName = "Document.pdf",
      scale = 2,
      format = "a4",
      orientation = "auto",
      title = "ডকুমেন্ট",
    } = options;

    if (!pdfRef.current) {
      message.error(`${title} খুঁজে পাওয়া যায়নি`);
      return;
    }

    try {
      setIsDownloading(true);
      message.loading({
        content: `${title} ডাউনলোড হচ্ছে...`,
        key: "download",
      });

      // Capture the content as canvas with high quality
      const canvas = await html2canvas(pdfRef.current, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        allowTaint: true,
      });

      // Calculate dimensions based on format
      const formatDimensions = {
        a4: { width: 297, height: 210 }, // A4 in mm
        letter: { width: 279, height: 216 }, // Letter in mm
      };

      const { width: pageWidth } = formatDimensions[format];

      // Calculate image dimensions
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Determine orientation
      let finalOrientation: "portrait" | "landscape" = "landscape";
      if (orientation === "auto") {
        finalOrientation = imgHeight > imgWidth ? "portrait" : "landscape";
      } else {
        finalOrientation = orientation;
      }

      // Create PDF
      const pdf = new jsPDF({
        orientation: finalOrientation,
        unit: "mm",
        format,
      });

      // Add image to PDF
      const imgData = canvas.toDataURL("image/png", 1.0);

      // Center the image if needed
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      let xOffset = 0;
      const yOffset = 0;
      let finalWidth = imgWidth;
      let finalHeight = imgHeight;

      // Fit image to page if larger
      if (imgHeight > pdfHeight) {
        finalHeight = pdfHeight;
        finalWidth = (canvas.width * finalHeight) / canvas.height;
        xOffset = (pdfWidth - finalWidth) / 2;
      }

      pdf.addImage(
        imgData,
        "PNG",
        xOffset,
        yOffset,
        finalWidth,
        finalHeight,
        undefined,
        "FAST"
      );

      // Sanitize filename
      const sanitizedFileName = fileName.endsWith(".pdf")
        ? fileName
        : `${fileName}.pdf`;

      // Download
      pdf.save(sanitizedFileName);

      message.success({
        content: `${title} ডাউনলোড সম্পন্ন হয়েছে`,
        key: "download",
      });
    } catch (error) {
      console.error("Download error:", error);
      message.error({
        content: "ডাউনলোড করতে সমস্যা হয়েছে",
        key: "download",
      });
    } finally {
      setIsDownloading(false);
    }
  }, []);

  return {
    pdfRef,
    isDownloading,
    downloadPdf,
  };
};

export default useDownloadPdf;
